import { createContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Question } from "src/api/types";
import Loading from "src/components/Loading/Loading";
import { MatchParam } from "src/constants/matches";
import { useTranslation } from "react-i18next";
import { useProduct } from "../ProductProvider";
import AnswerProvider from "../AnswerProvider";
import { useAnswers } from "../AnswersProvider";
import { useVisit } from "../VisitProvider";
import { usePatient } from "../PatientProvider";

export const QuestionsContext = createContext<Question | null>(null);

interface Props {
  children: React.ReactNode;
}

const QuestionsProvider = ({ children }: Props) => {
  const { t } = useTranslation();
  const { questionId } = useParams<MatchParam<"QUESTION_ID">>();
  const questions = useVisit((v) => v.questions);
  const gender = usePatient((p) => p.profile.gender);
  const allAnswers = useAnswers();
  const question = useMemo(() => {
    return questions?.find((q) => q.slug === questionId);
  }, [questionId, questions]);

  const productCategory = useProduct((p) => p.category);
  const slug = useProduct((p) => p.slug);

  const category = useMemo(
    () => t(`product-category.${productCategory.name}`, productCategory),
    [productCategory, t],
  );

  const displayName = useMemo(() => {
    if (slug === "ed-ot") {
      return t(`products.${slug}.display-name`);
    }
    return t(`products.${slug}.display-name`).toLowerCase();
  }, [slug, t]);

  const formattedQuestion = useMemo(() => {
    if (question == null) return null;

    return {
      ...question,
      title: t(`questions.${question.slug}.title`, question.title),
      subtitle: question.subtitle
        ? t(`questions.${question.slug}.subtitle`, question.subtitle, {
            category: category.toLowerCase(),
            product: displayName,
            gender,
          })
        : null,
      prompt: question.prompt
        ? t(`questions.${question.slug}.prompt`, question.prompt)
        : null,
      options:
        question.options
          ?.filter(({ conditions }) => {
            if (conditions != null) {
              const currentAnswer = allAnswers.find(
                (a) => a.question_id === conditions.depends_on,
              )?.response?.answer;

              if (currentAnswer == null) return true;

              return conditions.answer.some((ans) =>
                currentAnswer.includes(ans),
              );
            }

            return true;
          })
          .map((option) => ({
            ...option,
            label: t(`question_options.${option.slug}.title`, option.label),
          })) ?? null,
    };
  }, [allAnswers, category, displayName, gender, question, t]);

  if (question == null) {
    return <Loading />;
  }

  return (
    <QuestionsContext.Provider value={formattedQuestion}>
      <AnswerProvider>{children}</AnswerProvider>
    </QuestionsContext.Provider>
  );
};

export default QuestionsProvider;
